import endpoints from '@app/src/api/endpoints'
import { useCreateResource } from '@app/src/api/updateHooks'
import type { Option } from '@app/src/components/Form/Select/SimpleSelect'
import SimpleSelect from '@app/src/components/Form/Select/SimpleSelect'
import LoadingButton from '@app/src/components/LoadingButton'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import { getYears } from '@app/src/utils'
import { NotificationSeverity } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { ChevronRightOutlined } from '@mui/icons-material'
import { Card, CardActionArea, Grid, List, ListItem, ListItemText, Stack, Typography } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useConfirm } from 'material-ui-confirm'
import React, { useState } from 'react'
import { generatePath, useHistory } from 'react-router'

const PERIOD_OPTIONS: Option<number>[] = getYears(5, 1, 'DESC').map(year => ({
  label: year.toString(),
  value: year,
}))

const BenchmarkAdminConfig: React.FC = () => {
  const history = useHistory()
  const { enableAdminBenchmarkRecalculation } = useFlags()
  const { mutateAsync } = useCreateResource()
  const { showSnackbar } = useSnackbar()
  const confirm = useConfirm()
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<Option<number>>(
    PERIOD_OPTIONS.find(o => o.value === new Date().getFullYear()) ?? PERIOD_OPTIONS[0],
  )

  const handleRecalculate = async () => {
    try {
      await confirm({
        title: 'Recalculate benchmarks?',
        description: `All existing benchmark results for ${selectedOption.value} will be permanently removed to make way for the newly calculated ones. This cannot be undone.`,
        confirmationText: 'Recalculate',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
        cancellationText: 'Cancel',
      })
    } catch {
      return
    }

    setLoading(true)
    try {
      await mutateAsync(
        { url: `${endpoints.calculateBenchmarks}?periodName=${selectedOption.value}`, body: {} },
        {
          onSuccess: () => {
            showSnackbar({
              message: 'Benchmark recalculation triggered successfully',
              severity: NotificationSeverity.success,
            })
          },
          onError: () => {
            showSnackbar({
              message: 'Failed to trigger benchmark recalculation',
              severity: NotificationSeverity.error,
            })
          },
        },
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <ConfigurationCollection enablePadding={{ top: false, right: true }}>
      <List>
        <ListItem>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ListItemText
                primary="Recalculate benchmarks"
                secondary="Trigger benchmark recalculation for the Worldfavor Sourcing solution. This will re-evaluate all assessment benchmark rows (for assessments linked to sustainability topics and baseline assessment) and all findings benchmark rows, grouped by network, industry, and other dimensions."
              />
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1} alignItems="flex-end">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                  <Stack sx={{ minWidth: 160 }}>
                    <SimpleSelect<number>
                      fieldLabel="Period"
                      options={PERIOD_OPTIONS}
                      value={selectedOption}
                      onChange={value => {
                        if (value && !Array.isArray(value)) setSelectedOption(value)
                      }}
                      open={open}
                      setOpen={setOpen}
                      loading={false}
                      multiple={false}
                      enableSelectAll={false}
                      disableClearable
                      disabled={!enableAdminBenchmarkRecalculation}
                      variant="outlined"
                      size="medium"
                    />
                  </Stack>
                  <LoadingButton
                    variant="contained"
                    size="large"
                    onClick={handleRecalculate}
                    loading={loading}
                    disabled={!enableAdminBenchmarkRecalculation}
                  >
                    Recalculate benchmarks
                  </LoadingButton>
                </Stack>
                {!enableAdminBenchmarkRecalculation && (
                  <Typography variant="caption" color="text.secondary">
                    Controlled by the enable-admin-benchmark-recalculation feature flag.
                  </Typography>
                )}
              </Stack>
            </Grid>
          </Grid>
        </ListItem>
        <ListItem sx={{ mt: 8 }}>
          <Card variant="outlined" sx={{ width: '100%', borderRadius: 2 }}>
            <CardActionArea
              sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}
              onClick={() => history.push(generatePath(paths.benchmarks, { view: 'assessment' }))}
            >
              <Typography variant="body1">See all existing benchmark results</Typography>
              <ChevronRightOutlined />
            </CardActionArea>
          </Card>
        </ListItem>
      </List>
    </ConfigurationCollection>
  )
}

export default BenchmarkAdminConfig
